/**
 * utils/aiCrypto.ts
 * AES-GCM encryption utilities for AI credential storage
 *
 * Provides symmetric encryption/decryption of API keys using the Web Crypto API.
 * Keys are encrypted before storage in IndexedDB and decrypted only when needed
 * for API calls.
 *
 * BROWSER-ONLY: Requires secure context (HTTPS or localhost) for crypto.subtle.
 * Returns null from generateAESKey() if crypto.subtle is unavailable.
 *
 * @see src/types/shared/AI.ts for AICredential and AIEncryptionKeyRecord types
 * @see src/services/AICredentialService.ts for the service that uses these utilities
 */

// ─── Availability Check ─────────────────────────────────────────────────────

/**
 * Check if Web Crypto API (crypto.subtle) is available for AES-GCM operations.
 *
 * crypto.subtle requires a secure context (HTTPS) and isn't available
 * on all mobile browsers (e.g., iOS Chrome/Safari in certain contexts).
 */
export const isCryptoSubtleAvailable = (): boolean => {
  try {
    return (
      typeof crypto !== 'undefined' &&
      typeof crypto.subtle?.generateKey === 'function' &&
      typeof crypto.subtle?.encrypt === 'function' &&
      typeof crypto.subtle?.decrypt === 'function'
    );
  } catch {
    return false;
  }
};

// ─── Base64 Helpers ─────────────────────────────────────────────────────────

/**
 * Encode an ArrayBuffer to a base64 string
 *
 * @param buffer - Binary data to encode
 * @returns Base64-encoded string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

/**
 * Decode a base64 string to a Uint8Array
 *
 * @param base64 - Base64-encoded string to decode
 * @returns Decoded binary data
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ─── Key Management ─────────────────────────────────────────────────────────

/**
 * Generate a new AES-GCM 256-bit encryption key
 *
 * Creates a symmetric key for encrypting/decrypting AI API keys.
 * Called once per device; the key is then exported and stored in IndexedDB.
 *
 * Returns null if crypto.subtle is unavailable — callers decide how to handle.
 *
 * @returns Promise resolving to a CryptoKey, or null if unavailable
 *
 * @example
 * ```ts
 * const key = await generateAESKey();
 * if (key) {
 *   const exported = await exportKeyToBase64(key);
 *   // Store exported key in ai_encryption_keys table
 * }
 * ```
 */
export async function generateAESKey(): Promise<CryptoKey | null> {
  if (!isCryptoSubtleAvailable()) {
    return null;
  }

  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable — required for export/import cycle
    ['encrypt', 'decrypt']
  );
}

/**
 * Export a CryptoKey to a base64-encoded string for persistent storage
 *
 * Exports the raw key material so it can be stored in IndexedDB
 * (`AIEncryptionKeyRecord.exportedKey`). The key can be restored
 * later via `importKeyFromBase64()`.
 *
 * @param key - CryptoKey to export
 * @returns Promise resolving to base64-encoded key material
 */
export async function exportKeyToBase64(key: CryptoKey): Promise<string> {
  const rawKey = await crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64(rawKey);
}

/**
 * Import a CryptoKey from a base64-encoded string
 *
 * Restores a CryptoKey from the raw key material previously exported
 * via `exportKeyToBase64()`. Used to reload the device encryption key
 * from IndexedDB on session start.
 *
 * @param base64 - Base64-encoded raw key material
 * @returns Promise resolving to a CryptoKey ready for encrypt/decrypt
 */
export async function importKeyFromBase64(base64: string): Promise<CryptoKey> {
  const rawKey = base64ToUint8Array(base64);
  return crypto.subtle.importKey(
    'raw',
    rawKey.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: 256 },
    true, // extractable — matches generateAESKey
    ['encrypt', 'decrypt']
  );
}

// ─── Encrypt / Decrypt ──────────────────────────────────────────────────────

/** Result of an AES-GCM encryption operation */
export interface AESEncryptResult {
  /** Base64-encoded ciphertext */
  ciphertext: string;

  /** Base64-encoded initialization vector (12 bytes, unique per encryption) */
  iv: string;
}

/**
 * Encrypt a plaintext string using AES-GCM
 *
 * Generates a random 12-byte IV per call (required for AES-GCM security —
 * never reuse an IV with the same key). Returns both the ciphertext and IV
 * as base64 strings for storage.
 *
 * @param plaintext - String to encrypt (e.g., an API key)
 * @param key - AES-GCM CryptoKey for encryption
 * @returns Promise resolving to ciphertext and IV (both base64-encoded)
 *
 * @example
 * ```ts
 * const { ciphertext, iv } = await encryptString('sk-abc123...', cryptoKey);
 * // Store ciphertext and iv in AICredential record
 * ```
 */
export async function encryptString(
  plaintext: string,
  key: CryptoKey
): Promise<AESEncryptResult> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  // 12-byte IV is the recommended size for AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    key,
    data.buffer as ArrayBuffer
  );

  return {
    ciphertext: arrayBufferToBase64(ciphertextBuffer),
    iv: arrayBufferToBase64(iv.buffer as ArrayBuffer),
  };
}

/**
 * Decrypt a ciphertext string using AES-GCM
 *
 * Reverses the encryption performed by `encryptString()`. Requires the same
 * key and the IV that was generated during encryption.
 *
 * Throws if the key is wrong, the IV doesn't match, or the ciphertext
 * has been tampered with (AES-GCM provides authenticated encryption).
 *
 * @param ciphertext - Base64-encoded ciphertext from `encryptString()`
 * @param iv - Base64-encoded IV from `encryptString()`
 * @param key - AES-GCM CryptoKey used for the original encryption
 * @returns Promise resolving to the decrypted plaintext string
 *
 * @example
 * ```ts
 * const apiKey = await decryptString(credential.encryptedKey, credential.iv, cryptoKey);
 * // Use apiKey for API call, then discard
 * ```
 */
export async function decryptString(
  ciphertext: string,
  iv: string,
  key: CryptoKey
): Promise<string> {
  const ciphertextBytes = base64ToUint8Array(ciphertext);
  const ivBytes = base64ToUint8Array(iv);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
    key,
    ciphertextBytes.buffer as ArrayBuffer
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}
